import moment from "moment";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { findDashboardPayableAccounts } from "../../../repositories/qerpapi/erp/relatorio/ContasPagarRepository";
import { findDashboardReceivableAccounts } from "../../../repositories/qerpapi/erp/relatorio/ContasReceberRepository";
import { notify } from "../../../utils/notify";
import { findDashboardRecurrentServiceContract } from "../../../repositories/qerpapi/erp/relatorio/FaturamentoRepository";

const DashBoardFinanceiroContext = createContext();

const DashBoardFinanceiroContextProvider = ({ children }) => {
  const [cashFlowChartInfo, setCashFlowChartInfo] = useState({
    checkDisplayOptions: [
      {
        id: "previousBalance",
        check: true,
        name: "Saldo anterior",
      },
      {
        id: "notPayedAccounts",
        check: true,
        name: "Contas não pagas",
      },
      {
        id: "notReceivedTitles",
        check: true,
        name: "Contas não recebidas",
      },
    ],
    groupPeriod: "day",
    radioDateDisplayOptions: [
      {
        id: "day",
        name: "Diário",
      },
      {
        id: "week",
        name: "Semanal",
      },
      {
        id: "month",
        name: "Mensal",
      },
    ],
    data: [],
    isLoading: false,
  });

  const [billsChartsInfo, setBillsChartsInfo] = useState({
    actualStartDate: moment().startOf("month").format("YYYY-MM-DD"),
    actualEndDate: moment().endOf("month").format("YYYY-MM-DD"),
    actualPeriod: {
      description: "Usar Filtro Geral",
      value: "custom",
    },
    groupPeriod: "month",
    companies: sessionStorage.getItem("empresa_id"),
    radioDateDisplayOptions: [
      {
        id: "date",
        name: "Diário",
        disabled: true,
      },
      {
        id: "week",
        name: "Semanal",
        disabled: true,
      },
      {
        id: "month",
        name: "Mensal",
        disabled: false,
      },
    ],
    filterPeriod: "actual",
    periodsOptions: [
      {
        description: "Usar Filtro Geral",
        value: "custom",
      },
      {
        description: "Este Mês",
        value: "currentMonth",
      },
      {
        description: "Hoje",
        value: "today",
      },
      {
        description: "Mês Anterior",
        value: "lastMonth",
      },
      {
        description: "Próximo Mês",
        value: "nextMonth",
      },
      {
        description: "Este Ano",
        value: "currentYear",
      },
      {
        description: "Este Trimestre",
        value: "quarter",
      },
    ],
    radioPeriodDateFilterOptions: [
      {
        id: "actual",
        name: "Atual",
      },
      {
        id: "custom",
        name: "Personalizado",
      },
    ],
    statusLabels: {
      open: "Aberto",
      closed: "Pago",
      expired: "Vencido",
    },
    billsToPayChartView: "bar",
    billsToReceiveChartView: "bar",
    billsToPayData: [],
    billsToPayPieChartData: [],
    billsToReceiveData: [],
    billsToReceivePieChartData: [],
    billsToPayGroupPeriod: "month",
    billsToReceiveGroupPeriod: "month",
  });

  const [recurrentServiceData, setRecurrentServiceData] = useState(null);

  // -------------------------------------------------------------------------------------------------------

  const transformToPieData = useCallback((chartData) => {
    const transformed = Object.values(
      chartData.reduce((acc, obj) => {
        for (const key in obj) {
          if (key !== "name") {
            acc[key] = (acc[key] || 0) + obj[key];
          }
        }
        return acc;
      }, {})
    ).map((value, index) => ({
      name: Object.keys(chartData[0])[index + 1],
      value,
    }));

    return transformed;
  }, []);

  // -------------------------------------------------------------------------------------------------------

  const toggleCashFlowLoading = useCallback(
    (isLoading) =>
      setCashFlowChartInfo((prev) => ({
        ...prev,
        isLoading,
      })),
    []
  );

  const removeCreditInfo = useCallback((data) => {
    data.forEach((info) => {
      delete info.credit;
    });
  }, []);

  const validateIfHasCredit = useCallback(
    (data) => {
      const hasCredit = data.some((info) => info.credit);
      if (!hasCredit) removeCreditInfo(data);

      return data;
    },
    [removeCreditInfo]
  );

  // -------------------------------------------------------------------------------------------------------

  const setBillsToPayData = useCallback(
    (newValue) => {
      const emptyData = [
        {
          name: "",
          empty: 0,
        },
      ];
      let formattedValue = newValue;
      if (billsChartsInfo.billsToPayGroupPeriod !== "month") {
        formattedValue = newValue.map((info) => {
          return {
            ...info,
            name: moment(info.name, "DD/MM/YY").format("DD/MM"),
          };
        });
      }
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToPayData: formattedValue.length ? formattedValue : emptyData,
      }));
    },
    [billsChartsInfo.billsToPayGroupPeriod]
  );

  const setBillsToPayPieChartData = useCallback(
    (newValue) => {
      const transformed = transformToPieData(newValue);
      const emptyData = [
        {
          name: "open",
          value: 0,
        },
        {
          name: "closed",
          value: 0,
        },
        {
          name: "expired",
          value: 0,
        },
      ];

      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToPayPieChartData: transformed.length ? transformed : emptyData,
      }));
    },
    [transformToPieData]
  );

  const setBillsToReceiveData = useCallback(
    (newValue) => {
      const emptyData = [
        {
          name: "",
          empty: 0,
        },
      ];

      let formattedValue = newValue;
      if (billsChartsInfo.billsToReceiveGroupPeriod !== "month") {
        formattedValue = newValue.map((info) => {
          return {
            ...info,
            name: moment(info.name, "DD/MM/YY").format("DD/MM"),
          };
        });
      }
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToReceiveData: formattedValue.length ? formattedValue : emptyData,
      }));
    },
    [billsChartsInfo.billsToReceiveGroupPeriod]
  );

  const setBillsToReceivePieChartData = useCallback(
    (newValue) => {
      const transformed = transformToPieData(newValue);
      const emptyData = [
        {
          name: "open",
          value: 0,
        },
        {
          name: "closed",
          value: 0,
        },
        {
          name: "expired",
          value: 0,
        },
      ];
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToReceivePieChartData: transformed.length
          ? transformed
          : emptyData,
      }));
    },
    [transformToPieData]
  );

  const setBillsToPayChartView = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToPayChartView: newValue,
      })),
    []
  );

  const setBillsToReceiveChartView = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToReceiveChartView: newValue,
      })),
    []
  );

  const setBillsToPayGroupPeriod = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToPayGroupPeriod: newValue,
      })),
    []
  );

  const setBillsToReceiveGroupPeriod = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({
        ...prev,
        billsToReceiveGroupPeriod: newValue,
      })),
    []
  );

  const setBillsChartsInfoCompanies = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({ ...prev, companies: newValue })),
    []
  );

  const setBillsChartsGroupPeriod = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({ ...prev, groupPeriod: newValue })),
    []
  );

  const setBillsChartsSearchDate = useCallback(
    ({ startDate, endDate }) =>
      setBillsChartsInfo((prev) => ({
        ...prev,
        actualStartDate: startDate,
        actualEndDate: endDate,
      })),
    []
  );

  const setBillsChartsActualPeriod = useCallback(
    (newValue) =>
      setBillsChartsInfo((prev) => ({
        ...prev,
        actualPeriod: newValue,
      })),
    []
  );

  // -------------------------------------------------------------------------------------------------------

  const handleMainPayableAccounts = useCallback(
    async (res) => {
      const data = res?.data;

      if (!res?.success) {
        if (res.statusCode === 401) {
          return notify("Token expirado", true, "error");
        }
        return notify(res.message, true, "error");
      }

      const chartData = validateIfHasCredit(data?.movements);
      setBillsToPayPieChartData(chartData);
      setBillsToPayData(chartData);
    },
    [setBillsToPayData, setBillsToPayPieChartData, validateIfHasCredit]
  );

  const handleMainRecurrentService = useCallback(
    async (res) => {
      const data = res?.data;

      if (!res?.success) {
        if (res.statusCode === 401) {
          return notify("Token expirado", true, "error");
        }
        return notify(res.message, true, "error");
      }

      setRecurrentServiceData(data);
    },
    [setRecurrentServiceData]
  );

  const {
    isLoading: isBillToPayLoading,
    refetch: billToPayRefetch,
    isRefetching: isBillToPayRefetchLoading,
  } = useQuery(
    "contas-pagar-dashboard",
    () =>
      findDashboardPayableAccounts({
        multicompany: billsChartsInfo.companies,
        period: billsChartsInfo.actualPeriod.value,
        dateFilter: "expiration",
        sort: "date",
        sortDirection: "asc",
        startDate: billsChartsInfo.actualStartDate,
        endDate: billsChartsInfo.actualEndDate,
        group: billsChartsInfo.billsToPayGroupPeriod,
      }),
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!billsChartsInfo.companies,
      onSuccess: handleMainPayableAccounts,
    }
  );

  const { isLoading: isRecurrentServiceLoading } = useQuery(
    "servico-recorrente-dashboard",
    () =>
      findDashboardRecurrentServiceContract({
        multicompany: billsChartsInfo.companies,
      }),
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!billsChartsInfo.companies,
      onSuccess: handleMainRecurrentService,
    }
  );

  // -------------------------------------------------------------------------------------------------------

  const handleMainReceivableAccounts = useCallback(
    async (res) => {
      const data = res?.data;

      if (!res?.success) {
        if (res.statusCode === 401) {
          return notify("Token expirado", true, "error");
        }
        return notify(res.message, true, "error");
      }
      const chartData = validateIfHasCredit(data?.movements);
      setBillsToReceivePieChartData(chartData);
      setBillsToReceiveData(chartData);
    },
    [setBillsToReceiveData, setBillsToReceivePieChartData, validateIfHasCredit]
  );

  const {
    isLoading: isBillToReceiveLoading,
    refetch: billToReceiveRefetch,
    isRefetching: isBillToReceiveRefetchLoading,
  } = useQuery(
    "contas-receber-dashboard",
    () =>
      findDashboardReceivableAccounts({
        multicompany: billsChartsInfo.companies,
        period: billsChartsInfo.actualPeriod.value,
        dateFilter: "expiration",
        sort: "date",
        sortDirection: "asc",
        startDate: billsChartsInfo.actualStartDate,
        endDate: billsChartsInfo.actualEndDate,
        group: billsChartsInfo.billsToReceiveGroupPeriod,
      }),
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!billsChartsInfo.companies,
      onSuccess: handleMainReceivableAccounts,
    }
  );

  const contextValue = useMemo(
    () => ({
      isRecurrentServiceLoading,
      recurrentServiceData,
      cashFlowChartInfo,
      setCashFlowChartInfo,
      toggleCashFlowLoading,
      billsChartsInfo,
      setBillsChartsInfo,
      isBillToPayLoading,
      billToPayRefetch,
      isBillToPayRefetchLoading,
      isBillToReceiveLoading,
      billToReceiveRefetch,
      isBillToReceiveRefetchLoading,
      setBillsChartsInfoCompanies,
      setBillsChartsGroupPeriod,
      setBillsChartsSearchDate,
      setBillsToPayChartView,
      setBillsToReceiveChartView,
      setBillsChartsActualPeriod,
      setBillsToPayGroupPeriod,
      setBillsToReceiveGroupPeriod,
    }),
    [
      recurrentServiceData,
      isRecurrentServiceLoading,
      cashFlowChartInfo,
      toggleCashFlowLoading,
      billsChartsInfo,
      isBillToPayLoading,
      billToPayRefetch,
      isBillToPayRefetchLoading,
      isBillToReceiveLoading,
      billToReceiveRefetch,
      isBillToReceiveRefetchLoading,
      setBillsChartsInfoCompanies,
      setBillsChartsGroupPeriod,
      setBillsChartsSearchDate,
      setBillsToPayChartView,
      setBillsToReceiveChartView,
      setBillsChartsActualPeriod,
      setBillsToPayGroupPeriod,
      setBillsToReceiveGroupPeriod,
    ]
  );

  return (
    <DashBoardFinanceiroContext.Provider value={contextValue}>
      {children}
    </DashBoardFinanceiroContext.Provider>
  );
};

const useDashBoardFinanceiroService = () => {
  const context = useContext(DashBoardFinanceiroContext);

  if (!context) {
    throw new Error(
      "useDashBoardFinanceiroService must be used with a DashBoardFinanceiroContextProvider"
    );
  }

  return context;
};

export { DashBoardFinanceiroContextProvider, useDashBoardFinanceiroService };
